import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShoppingCart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: 'spring', stiffness: 300 }}
      className="card-product group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.slug}`}>
        {/* Image Container */}
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-4">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {discount && (
              <Badge className="bg-brand-orange text-white font-bold px-2 py-1">
                -{discount}%
              </Badge>
            )}
            {product.isNewArrival && (
              <Badge className="bg-brand-blue text-white font-bold px-2 py-1">
                NEW
              </Badge>
            )}
            {product.isBestSeller && !discount && (
              <Badge className="bg-brand-green text-white font-bold px-2 py-1">
                Best Seller
              </Badge>
            )}
          </div>
          {/* Quick Add Button */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileHover={{ scale: 1.05 }}
            className="absolute bottom-3 right-3 bg-white text-brand-orange p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            onClick={handleAddToCart}
            data-testid={`quick-add-${product.id}`}
          >
            <ShoppingCart className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Product Info */}
        <div className="space-y-2">
          {/* Rating */}
          <div className="flex items-center gap-1">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.floor(product.rating)
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-gray-500">({product.reviewCount})</span>
          </div>

          {/* Name */}
          <h3 className="font-heading font-semibold text-gray-800 group-hover:text-brand-orange transition-colors">
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex items-center gap-2">
            <span className="font-bold text-lg text-brand-orange">
              ${product.price.toFixed(2)}
            </span>
            {product.originalPrice && (
              <span className="text-sm text-gray-400 line-through">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Add to Cart Button */}
      <Button
        onClick={handleAddToCart}
        className="w-full mt-4 btn-primary"
        data-testid={`add-to-cart-${product.id}`}
      >
        <ShoppingCart className="w-4 h-4 mr-2" />
        Add to Cart
      </Button>
    </motion.div>
  );
};
